import React, { useState } from 'react';
import { useLocalize } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  ListRow,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { msgs } from '../../utilities/messages';

import DeleteKeyModal from './components/DeleteKeyModal';
import KeyRowCenter from './components/KeyRowCenter';
import KeyRowTrailing from './components/KeyRowTrailing';
import useAccessKeys from './hooks/useAccessKeys';
import { BLUE_KEY_SLOT, MAX_KEYS } from './constants';
import DownloadKeysModal from './DownloadKeysModal';

import '../ListRow/ListRow.css';
import './AccessKeysSection.css';

function AccessKeysSection({
  setAlertBannerData,
  sshKeyExpiryDurationInHours,
  sshKeyPassphraseMandatory,
  sshKeyPassphraseMinLength,
  sshKeyPassphraseMaxLength,
}) {
  const localize = useLocalize();
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const {
    accessKeys,
    isLoading,
    nextAvailableSlot,
    atKeyLimit,
    fetchKeys,
    deleteConfirmOpen,
    isDeleting,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
  } = useAccessKeys({ setAlertBannerData, localize });

  return (
    <section
      aria-label={localize(msgs.pam.apiKeys.sectionTitle)}
      className='AccessKeysSection'>
      {/* Section title */}
      <div className='LandingPage-listrow-section-title' aria-hidden='true'>
        <div className='LandingPage-listrow-section-title-startLine' />
        <div className='LandingPage-listrow-section-title-label'>
          {localize(msgs.pam.apiKeys.sectionTitle)}
        </div>
        <div className='LandingPage-listrow-section-title-endLine' />
      </div>

      <Box className='AccessKeysSection-card'>
        {/* Header */}
        <Box className='AccessKeysSection-header'>
          <Box className='AccessKeysSection-headerLeading'>
            <Icon kind='WebAppConsole' size='medium' />
            <Box className='AccessKeysSection-headerText'>
              <Typography kind='h3' lineLimit={1}>
                {localize(msgs.pam.apiKeys.headerTitle)}
              </Typography>
              <Typography
                kind='body2'
                lineLimit={1}
                color='--color-foreground-neutral-medium'>
                {localize(msgs.pam.apiKeys.headerDescription)}
              </Typography>
            </Box>
          </Box>
          <Box className='AccessKeysSection-headerAction'>
            <Tooltip
              text={localize(msgs.pam.apiKeys.deleteKeyLimitNote)}
              placement='top'
              isDisabled={!atKeyLimit}
              trigger={
                <Button
                  type='button'
                  kind='outlined'
                  size='medium'
                  onClick={() => setDownloadModalOpen(true)}
                  isDisabled={atKeyLimit}
                  leftIcon={<Icon kind='Download' size='smallMedium' />}>
                  {localize(msgs.pam.apiKeys.downloadNewKey)}
                </Button>
              }
            />
          </Box>
        </Box>
        {/* Key list */}
        <Box
          className='AccessKeysSection-keys'
          role='region'
          aria-label={localize(msgs.pam.apiKeys.headerTitle)}
          aria-busy={isLoading}>
          {isLoading && (
            <Box className='AccessKeysSection-loader'>
              <Loader kind='dots' format='inline' color='info' size='medium' />
            </Box>
          )}
          {!isLoading && accessKeys.length === 0 && (
            <Box className='AccessKeysSection-emptyState'>
              <Icon
                kind='Credentials'
                size='smallMedium'
                color='--color-foreground-neutral-subtle'
                className='AccessKeysSection-emptyStateIcon'
              />
              <Typography
                kind='body2'
                lineLimit={2}
                color='--color-foreground-neutral-subtle'>
                {localize(msgs.pam.apiKeys.emptyState)}
              </Typography>
            </Box>
          )}
          {!isLoading && accessKeys.length > 0 && (
            <>
              {accessKeys.map((key) => {
                const iconColor =
                  key.slot === BLUE_KEY_SLOT
                    ? '--color-foreground-blue-medium'
                    : '--color-foreground-success-medium';

                return (
                  <ListRow
                    key={key.slot}
                    className='AccessKeysSection-listRow'
                    leadingComp={
                      <Icon
                        kind='Credentials'
                        size='medium'
                        color={iconColor}
                        aria-hidden='true'
                      />
                    }
                    centerContent={<KeyRowCenter keyItem={key} />}
                    trailingContent={
                      <KeyRowTrailing
                        keyItem={key}
                        onDeleteClick={handleDeleteClick}
                        onTimerExpired={fetchKeys}
                        localize={localize}
                      />
                    }
                    isListItem={false}
                    isCheckboxDisabled
                  />
                );
              })}
              <Typography
                kind='body3'
                className='AccessKeysSection-keyCountNote'>
                {localize(msgs.pam.apiKeys.keysUsed, {
                  count: accessKeys.length,
                  max: MAX_KEYS,
                })}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      {/* Download Access Keys modal */}
      {downloadModalOpen && (
        <DownloadKeysModal
          isOpen={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          onSubmit={fetchKeys}
          setAlertBannerData={setAlertBannerData}
          nextKeySlot={nextAvailableSlot}
          sshKeyExpiryDurationInHours={sshKeyExpiryDurationInHours}
          sshKeyPassphraseMandatory={sshKeyPassphraseMandatory}
          sshKeyPassphraseMinLength={sshKeyPassphraseMinLength}
          sshKeyPassphraseMaxLength={sshKeyPassphraseMaxLength}
        />
      )}
      {/* Delete confirmation modal */}
      <DeleteKeyModal
        isOpen={deleteConfirmOpen}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </section>
  );
}

AccessKeysSection.propTypes = {
  setAlertBannerData: PropTypes.func.isRequired,
  sshKeyExpiryDurationInHours: PropTypes.number,
  sshKeyPassphraseMandatory: PropTypes.bool,
  sshKeyPassphraseMinLength: PropTypes.number,
  sshKeyPassphraseMaxLength: PropTypes.number,
};

AccessKeysSection.defaultProps = {
  sshKeyExpiryDurationInHours: undefined,
  sshKeyPassphraseMandatory: undefined,
  sshKeyPassphraseMinLength: undefined,
  sshKeyPassphraseMaxLength: undefined,
};

export default AccessKeysSection;
